import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UserDashboard from './pages/UserDashboard';
import CreateEvent from './pages/CreateEvent';
import GuidedEventBuilder from './pages/GuidedEventBuilder';
import EditEvent from './pages/EditEvent';
import ManageEvent from './pages/ManageEvent';
import EventPage from './pages/EventPage';
import TicketDisplay from './pages/TicketDisplay';
import CheckIn from './pages/CheckIn';
import AdminDashboard from './pages/AdminDashboard';
import DemoEvent from './pages/DemoEvent';
import Account from './pages/Account';
import SetupDefaults from './pages/SetupDefaults';
import PassTiers from './pages/PassTiers';
import PaymentMethods from './pages/PaymentMethods';
import PlatformSettings from './pages/PlatformSettings';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import SelfCheckIn from './pages/SelfCheckIn';
import MasterCheckIn from './pages/MasterCheckIn';
import CalendarPage from './pages/CalendarPage';
import ActivityPage from './pages/ActivityPage';
import ReportsPage from './pages/ReportsPage';
import EventDetailView from './components/reports/EventDetailView';
import GuestDetailView from './components/reports/GuestDetailView';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import FindPass from './pages/FindPass';
import RecoverPass from './pages/RecoverPass';
import ClaimPass from './pages/ClaimPass';
import Redeem from './pages/Redeem';
import Guide from './pages/Guide';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import StripeConnectSettings from './pages/StripeConnectSettings';
import ScrollToTop from './components/ScrollToTop';

const HOST_ADMIN = ['host', 'admin'];

function PostLoginRouter({ user }) {
  const role = user?.role || 'user';
  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role === 'host') return <Navigate to="/dashboard" replace />;
  return <Navigate to="/my-tickets" replace />;
}

// Detect OAuth callback synchronously before ProtectedRoute runs
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/e/:event_id" element={<EventPage />} />
          <Route path="/e/:id/checkin" element={<SelfCheckIn />} />
          <Route path="/h/:host_id/checkin" element={<MasterCheckIn />} />
          <Route path="/ticket/:ticket_id" element={<TicketDisplay />} />
          <Route path="/demo" element={<DemoEvent />} />

          {/* Public legal / support */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/find-pass" element={<FindPass />} />
          <Route path="/recover-pass/:token" element={<RecoverPass />} />
          <Route path="/claim" element={<ClaimPass />} />
          <Route path="/redeem" element={<Redeem />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/settings/payments" element={
            <ProtectedRoute requiredRole="host"><StripeConnectSettings /></ProtectedRoute>
          } />

          {/* Post-login router */}
          <Route path="/home" element={
            <ProtectedRoute><PostLoginRouter /></ProtectedRoute>
          } />

          {/* User routes */}
          <Route path="/my-tickets" element={
            <ProtectedRoute><UserDashboard /></ProtectedRoute>
          } />

          {/* Host/Admin routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><Dashboard /></ProtectedRoute>
          } />
          <Route path="/events/create" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><CreateEvent /></ProtectedRoute>
          } />
          <Route path="/create-event/guided" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><GuidedEventBuilder /></ProtectedRoute>
          } />
          <Route path="/events/:id/edit" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><EditEvent /></ProtectedRoute>
          } />
          <Route path="/events/:id/manage" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><ManageEvent /></ProtectedRoute>
          } />
          <Route path="/setup" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><SetupDefaults /></ProtectedRoute>
          } />
          <Route path="/pass-tiers" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><PassTiers /></ProtectedRoute>
          } />
          <Route path="/payment-methods" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><PaymentMethods /></ProtectedRoute>
          } />
          <Route path="/settings/platform" element={
            <ProtectedRoute requiredRole="admin"><PlatformSettings /></ProtectedRoute>
          } />
          <Route path="/activity" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><ActivityPage /></ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><ReportsPage /></ProtectedRoute>
          } />
          <Route path="/reports/event/:event_id" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><EventDetailView /></ProtectedRoute>
          } />
          <Route path="/reports/guest/:email" element={
            <ProtectedRoute requiredRole={HOST_ADMIN}><GuestDetailView /></ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute requiredRole="admin"><ReportsPage /></ProtectedRoute>
          } />

          {/* Check-in (host, admin, or assigned staff) */}
          <Route path="/events/:id/check-in" element={
            <ProtectedRoute><CheckIn /></ProtectedRoute>
          } />

          {/* Admin only */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>
          } />

          {/* Shared */}
          <Route path="/account" element={
            <ProtectedRoute><Account /></ProtectedRoute>
          } />
          <Route path="/calendar" element={
            <ProtectedRoute><CalendarPage /></ProtectedRoute>
          } />
          <Route path="/guide" element={
            <ProtectedRoute><Guide /></ProtectedRoute>
          } />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
